/**
 * Action Center — unified ActionItem model + stream adapters (DEV-1201,
 * POLICY_ACTION_CENTER.md). Pure, framework-free so it is unit-testable and
 * so the priority score lives in one place.
 *
 * Four source streams collapse into one ActionItem list:
 *   - events       ← workflow_requests (invitations / approvals / governance / notices)
 *   - rhythm tasks ← role_rhythm_task_instances (recurring + standing)
 *   - data entry   ← per-project KPI rows (one item per project + section, per Q6)
 *   - system tasks ← onboarding milestones
 *
 * Priority score (POLICY_ACTION_CENTER.md §Q3, "Balanced" default):
 *   score = type base
 *         + (OVERDUE_W   × days late)
 *         + (DUE_SOON_W  × days inside a LEAD-day window)
 *         + (WAITING_W   × days an event has waited)
 */

import type { WorkflowRequest } from "../../workflow";
import type { RoleRhythmInstanceResponse } from "../../api/roleRhythms";
import type { MyMiniProjectTask } from "../../api/miniProjectTasks";
import type { MyRaciTask } from "../../api/useMyRaciTasks";

export type ActionKind =
  | "invitation"
  | "approval"
  | "governance"
  | "notice"
  | "data"
  | "rhythm"
  | "standing"
  | "mp_task"
  | "raci"
  | "system";

export type ActionStatus =
  | "needs_response"
  | "due"
  | "overdue"
  | "snoozed"
  | "ongoing"
  | "resolved";

/**
 * Pillar of the entity a role is assigned to (SPEC_ROLE_ORGANIZED_INBOX §4a).
 * Drives the role-tile color in the role-organized Inbox. "base" = role #0
 * (Life4Earth — {rung}); "other" = unresolved attribution (visible, never dropped).
 * Distinct from the management status palette — pillar is identity, not status.
 */
export type RolePillar =
  | "community"
  | "enabler"
  | "project"
  | "problem"
  | "solution"
  | "base"
  | "other";

/** Reserved synthetic grouping key for the base "L4E User" role #0. */
export const ROLE_BASE_KEY = "role:base";
/** Grouping key for items whose owning role cannot (yet) be resolved. Visible. */
export const ROLE_OTHER_KEY = "other";

export type DataSection = "schedule" | "finance" | "mission";

export interface ActionDataRef {
  projectGuid: string;
  projectName: string;
  section: DataSection;
  row: any;
}

export interface ActionItem {
  id: string;
  kind: ActionKind;
  /** i18n key (namespace 'pages') for the Type chip label. */
  typeKey: string;
  /** Source column — person (events) or org/project (obligations). */
  source: string;
  /** Role column — the role this item belongs to (rhythm tasks); else undefined. */
  role?: string;
  /**
   * RACI classification of THIS action for the current user (DEV-1368).
   * Cadence / data-entry / system obligations are the user's own to do, so
   * they are "R" (Responsible). Gantt-derived rows can carry A/C/I once that
   * source feeds the inbox. Undefined is rendered as "R".
   */
  raci?: "R" | "A" | "C" | "I";
  /**
   * SPEC_ROLE_ORGANIZED_INBOX §3 — canonical grouping key for the role-organized
   * Inbox. Stable identifier, NOT a display string: `role:base` | `community:{GUID}`
   * | `community:name:{label}` (fallback when no GUID) | `other`. Undefined is
   * treated as `other` by groupActionItems so attribution gaps stay visible.
   */
  roleGroupKey?: string;
  /** Display label for the role group (community/entity name, or "Life4Earth"). */
  roleGroupLabel?: string;
  /**
   * Role pillar — the pillar of the entity the role is assigned to (§4a).
   * "base" for role #0. The container pillar (white community / blue org) and the
   * exact community-vs-org distinction are resolved in the page via an entity-type
   * lookup (pillarForEntityType); left undefined here when not yet derivable.
   */
  pillar?: RolePillar;
  /** Lead time in days (rhythm tasks) — shown in the tray to explain timing. */
  leadTimeDays?: number;
  /** Cadence type (rhythm tasks) — tray shows "how often". */
  cadenceType?: string;
  /** Task description (rhythm tasks) — shown in the tray. */
  description?: string;
  /** Community/org GUID (rhythm tasks) — powers the tray "Open community" button. */
  communityGuid?: string;
  /** Subject column. */
  subject: string;
  /** Date column display + sortable epoch ms (large = no date → sorts last). */
  dateLabel: string;
  dateSort: number;
  /** "Added" column — when the item entered the Action Center. Events: created_at;
   *  rhythm: when it crossed into its lead window (due − lead); mp_task: created_at;
   *  data/system: none ("—"). Sortable via addedSort (large = none → sorts last). */
  addedLabel?: string;
  addedSort?: number;
  status: ActionStatus;
  overdue: boolean;
  // ── scoring inputs ──
  base: number;
  overdueDays: number;
  dueInDays: number | null;
  ageDays: number;
  // ── payload back-references (one is populated per kind) ──
  workflow?: WorkflowRequest;
  instance?: RoleRhythmInstanceResponse;
  data?: ActionDataRef;
  milestone?: any;
  /** DEV-1257. Mini-project task back-reference. Populated only when
   *  kind === "mp_task". Carries the full task + parent MP name so the
   *  tray can render details and complete in place. */
  mpTask?: MyMiniProjectTask;
  /** DEV-1368. Gantt RACI task back-reference. Populated only when
   *  kind === "raci". Carries the project/role tag + RACI state so the
   *  tray can render a read-only detail (and, in the follow-up, the
   *  respond / acknowledge / mark-done / flag actions). */
  raciTask?: MyRaciTask;
  /**
   * Onboarding chunk A (§7.7/§7.9). Marks a Getting-started checklist row.
   * Completed checklist rows stay VISIBLE in the To-do list with the
   * handled check (locked §7.10 r7: rows the user already satisfies render
   * complete, not pending) — the page's resolved-filter exempts them.
   */
  gettingStarted?: boolean;
  /** Checklist row key ('complete_profile' | 'follow_three' | 'meet_org' |
   *  'org_guidelines' | 'promotion_notice') — drives tray branching. */
  checklistKey?: string;
}

// ── Priority weights (Balanced default; tunable) ───────────────
export const PRIORITY_WEIGHTS = { overdue: 3, dueSoon: 1.5, waiting: 2 };
const LEAD = 14;

// ── Type base importance ───────────────────────────────────────
// mp_task tracks rhythm: both are concrete operational obligations with a
// real due date and a Done action. Same base = same default priority
// before lateness adjustments apply.
const TYPE_BASE: Record<ActionKind, number> = {
  governance: 14,
  invitation: 12,
  approval: 10,
  data: 8,
  rhythm: 8,
  mp_task: 8,
  raci: 8,
  system: 3,
  notice: 2,
  standing: 0,
};

const TYPE_KEY: Record<ActionKind, string> = {
  invitation: "actionCenter.inbox.type.invitation",
  approval: "actionCenter.inbox.type.approval",
  governance: "actionCenter.inbox.type.governance",
  notice: "actionCenter.inbox.type.notice",
  data: "actionCenter.inbox.type.dataEntry",
  rhythm: "actionCenter.inbox.type.rhythmTask",
  standing: "actionCenter.inbox.type.rhythmTask",
  mp_task: "actionCenter.inbox.type.task",
  raci: "actionCenter.inbox.type.projectTask",
  system: "actionCenter.inbox.type.systemTask",
};

/** Type-chip i18n key for a kind. Exported so the Sent/Activity view can label
 *  server-sourced completed items with the same chips the inbox uses. */
export function typeKeyFor(kind: ActionKind): string {
  return TYPE_KEY[kind];
}

/** Events are the kinds whose age (time waiting) feeds the score. */
const EVENT_KINDS: ReadonlySet<ActionKind> = new Set([
  "invitation",
  "approval",
  "governance",
  "notice",
]);

export function actionItemScore(it: ActionItem): number {
  let s = it.base;
  if (it.overdueDays > 0) s += PRIORITY_WEIGHTS.overdue * it.overdueDays;
  if (it.dueInDays !== null && it.dueInDays >= 0) {
    s += PRIORITY_WEIGHTS.dueSoon * Math.max(0, LEAD - it.dueInDays);
  }
  if (EVENT_KINDS.has(it.kind) && it.ageDays > 0) {
    s += PRIORITY_WEIGHTS.waiting * it.ageDays;
  }
  return s;
}

// ════════════════════════════════════════════════════════════════
// Role grouping (SPEC_ROLE_ORGANIZED_INBOX §4 / §4a)
// ════════════════════════════════════════════════════════════════

/**
 * Map an owning-entity type to its pillar (SPEC_ROLE_ORGANIZED_INBOX §4a).
 * geo_* communities → Community (white); org_* → Enabler (blue); the rest carry
 * their own pillar. Pure so the page can resolve container/role pillar from the
 * communities list (which carries entity_type) without coupling this module to it.
 */
export function pillarForEntityType(
  entityType: string | null | undefined,
): RolePillar {
  const e = (entityType || "").toLowerCase();
  if (e.startsWith("geo")) return "community";
  if (e.startsWith("org")) return "enabler";
  if (e.includes("mini_project") || e.startsWith("project")) return "project";
  if (e.startsWith("problem") || e.includes("workspace")) return "problem";
  if (e.startsWith("solution")) return "solution";
  return "other";
}

export interface ActionGroup {
  /** roleGroupKey shared by every item in the group. */
  key: string;
  /** Display label (community/entity name, "Life4Earth", or "Other"). */
  label: string;
  items: ActionItem[];
  /** Priority score of the group's top item — drives between-section ordering. */
  topScore: number;
}

/**
 * Group items by roleGroupKey. Sections are ordered by their top item's priority
 * score, and items within a section by score — grouping NESTS priority, it never
 * replaces it (POLICY_ACTION_CENTER §Q3 as amended by SPEC_ROLE_ORGANIZED_INBOX §4).
 * Items with no roleGroupKey fall into a single visible ROLE_OTHER_KEY group so
 * attribution gaps are never silently dropped (§3 fallback rule).
 */
export function groupActionItems(items: ActionItem[]): ActionGroup[] {
  const map = new Map<string, ActionGroup>();
  for (const it of items) {
    const key = it.roleGroupKey || ROLE_OTHER_KEY;
    let g = map.get(key);
    if (!g) {
      g = { key, label: it.roleGroupLabel || "", items: [], topScore: -Infinity };
      map.set(key, g);
    }
    g.items.push(it);
  }
  const groups = Array.from(map.values());
  for (const g of groups) {
    g.items.sort((a, b) => actionItemScore(b) - actionItemScore(a));
    g.topScore = g.items.length ? actionItemScore(g.items[0]) : -Infinity;
    if (!g.label) g.label = g.items[0]?.roleGroupLabel || g.items[0]?.source || "";
  }
  groups.sort((a, b) => b.topScore - a.topScore);
  return groups;
}

// ════════════════════════════════════════════════════════════════
// Small resolvers (compact — full description logic stays in the page)
// ════════════════════════════════════════════════════════════════

export function buildNameLookup(
  items: Record<string, any>[] | undefined,
  guidKeys: string[],
  nameKeys: string[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items || []) {
    let guid = "";
    for (const k of guidKeys) {
      guid = String(item[k] || "");
      if (guid) break;
    }
    if (!guid) continue;
    let name = "";
    for (const k of nameKeys) {
      name = String(item[k] || "");
      if (name) break;
    }
    if (name) map.set(guid.toUpperCase(), name);
  }
  return map;
}

const DAY_MS = 86_400_000;
const NO_DATE = Number.MAX_SAFE_INTEGER;

function daysBetween(fromIso: string | null | undefined, to: Date): number {
  if (!fromIso) return 0;
  const t = new Date(fromIso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.round((to.getTime() - t) / DAY_MS);
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ════════════════════════════════════════════════════════════════
// Events  (workflow_requests → invitation / approval / governance / notice)
// ════════════════════════════════════════════════════════════════

export function eventKind(actionType: string): ActionKind {
  const a = actionType || "";
  if (
    a.endsWith(".role_accepted") ||
    a.endsWith(".role_declined") ||
    a.endsWith(".role_revoked") ||
    a.endsWith(".role_resignation") ||
    a.endsWith(".slot_role_accepted") ||
    a.endsWith(".slot_role_declined") ||
    a.endsWith(".slot_role_resignation")
  ) {
    return "notice";
  }
  if (a.endsWith(".role_assignment") || a.endsWith(".slot_role_assignment")) {
    return "invitation";
  }
  // DEV-1414: org membership admission. The invitation goes to the invitee
  // (accept/decline); the join request is an Approval in the admins' court.
  // membership_request needs no case — it falls through to "approval".
  if (a === "community.membership_invitation") {
    return "invitation";
  }
  if (
    a === "project.ownership_transfer" ||
    a === "community.ownership_transfer" ||
    a === "community.pm_trust_promotion" ||
    a === "impact.admin_grant"
  ) {
    return "governance";
  }
  // content publish, campaign qualify, completion verification, domain appeal, etc.
  return "approval";
}

const TERMINAL = new Set([
  "approved",
  "rejected",
  "cancelled",
  "rescinded",
  "resigned",
  "revoked",
  "acknowledged",
]);

export function isWorkflowResolved(w: WorkflowRequest): boolean {
  return TERMINAL.has(w.status);
}

/** Subject for an event row: prefer the stored title, else a per-kind generic. */
function eventSubject(
  w: WorkflowRequest,
  kind: ActionKind,
  t: (k: string) => string,
): string {
  // Invitations: lead with the role being offered (the payload carries
  // role_label) so the row says "Secretary" rather than a generic title.
  // The full "invited you to X on {community}" sentence shows in the tray.
  if (kind === "invitation") {
    const rl = w.payload && (w.payload as any).role_label
      ? String((w.payload as any).role_label).trim()
      : "";
    if (rl) return rl;
  }
  const title = (w.title || "").trim();
  if (title) return title;
  return t(`actionCenter.inbox.generic.${kind}`);
}

export interface EventAdapterCtx {
  currentUserGuid: string;
  resolveName: (guid: string | null | undefined) => string;
  t: (k: string) => string;
  now: Date;
}

export function eventToActionItem(
  w: WorkflowRequest,
  ctx: EventAdapterCtx,
): ActionItem {
  const kind = eventKind(w.action_type);
  const created = w.created_at;
  const ageDays = Math.max(0, daysBetween(created, ctx.now));
  const fromName =
    ctx.resolveName(w.created_by) || String(w.created_by || "").slice(0, 8);
  return {
    id: w.workflow_guid,
    kind,
    typeKey: TYPE_KEY[kind],
    source: fromName,
    subject: eventSubject(w, kind, ctx.t),
    dateLabel: shortDate(created),
    dateSort: created ? new Date(created).getTime() : NO_DATE,
    // Events have no due date; their meaningful date IS when they arrived.
    addedLabel: shortDate(created),
    addedSort: created ? new Date(created).getTime() : NO_DATE,
    status: kind === "notice" ? "needs_response" : "needs_response",
    overdue: false,
    base: TYPE_BASE[kind],
    overdueDays: 0,
    dueInDays: null,
    ageDays,
    workflow: w,
  };
}

// ════════════════════════════════════════════════════════════════
// Rhythm tasks  (role_rhythm_task_instances → rhythm / standing)
// ════════════════════════════════════════════════════════════════

export function rhythmToActionItem(
  inst: RoleRhythmInstanceResponse,
  ctx: { t: (k: string) => string; now: Date },
): ActionItem {
  const standing = inst.taskKind === "standing_duty";
  const kind: ActionKind = standing ? "standing" : "rhythm";
  // sourceLabel arrives as "Community Name — Role Label" (DEV-1164). Split so
  // the Source column shows the org and the Role column shows the role.
  let src = inst.sourceLabel || ctx.t("actionCenter.inbox.source.roleRhythm");
  let role = "";
  if (inst.sourceLabel) {
    const sep = inst.sourceLabel.indexOf(" — ");
    if (sep >= 0) {
      src = inst.sourceLabel.slice(0, sep);
      role = inst.sourceLabel.slice(sep + 3);
    }
  }
  let status: ActionStatus;
  let overdueDays = 0;
  let dueInDays: number | null = null;
  if (standing) {
    status = "ongoing";
  } else if (inst.status === "overdue") {
    status = "overdue";
    overdueDays = Math.max(1, daysBetween(inst.dueAt, ctx.now));
  } else if (inst.status === "snoozed") {
    status = "snoozed";
    dueInDays = inst.dueAt ? Math.max(0, -daysBetween(inst.dueAt, ctx.now)) : null;
  } else {
    status = "due";
    dueInDays = inst.dueAt ? Math.max(0, -daysBetween(inst.dueAt, ctx.now)) : null;
  }
  // "Added" = when the task crossed into its lead window (due − lead days) —
  // the moment it surfaced in the Action Center. Standing duties have no due,
  // so no entry date.
  let addedLabel = "—";
  let addedSort = NO_DATE;
  if (!standing && inst.dueAt) {
    const dueMs = new Date(inst.dueAt).getTime();
    if (!Number.isNaN(dueMs)) {
      addedSort = dueMs - (inst.leadTimeDays || 0) * DAY_MS;
      addedLabel = shortDate(new Date(addedSort).toISOString());
    }
  }
  // Role grouping (§4a): key by the owning community GUID (stable) when present,
  // else by community name as a fallback. The role/container pillar (community vs
  // org) is resolved in the page from the communities entity_type lookup.
  const roleGroupKey = inst.communityEntityGuid
    ? `community:${inst.communityEntityGuid.toUpperCase()}`
    : src
      ? `community:name:${src}`
      : ROLE_OTHER_KEY;
  return {
    id: inst.instanceGuid,
    kind,
    typeKey: TYPE_KEY[kind],
    source: src,
    role,
    roleGroupKey,
    roleGroupLabel: src,
    leadTimeDays: inst.leadTimeDays,
    cadenceType: inst.cadenceType ?? undefined,
    description: inst.description ?? undefined,
    communityGuid: inst.communityEntityGuid ?? undefined,
    subject: inst.taskName,
    dateLabel: standing
      ? ctx.t("actionCenter.inbox.ongoing")
      : shortDate(inst.dueAt),
    dateSort: !standing && inst.dueAt ? new Date(inst.dueAt).getTime() : NO_DATE,
    addedLabel,
    addedSort,
    status,
    overdue: status === "overdue",
    base: TYPE_BASE[kind],
    overdueDays,
    dueInDays,
    ageDays: 0,
    instance: inst,
  };
}

// ════════════════════════════════════════════════════════════════
// Mini-project tasks  (mini_project_tasks → "Task" type, DEV-1257)
// ════════════════════════════════════════════════════════════════
//
// "Open" = status IN ('not_started','in_progress') (filtered server-side).
// Lateness classification mirrors rhythm: due_date past → overdue;
// due_date in the future → due; no due_date → ongoing (no urgency
// signal to compute against). Source column = parent MP title.

export function miniProjectTaskToActionItem(
  row: MyMiniProjectTask,
  ctx: { t: (k: string) => string; now: Date },
): ActionItem {
  const task = row.task;
  // Subject: prefer NameText (custom Lead-authored task), fall back to
  // NameKey-as-i18n-key (template task). If neither resolves we emit the
  // raw key so the row is still recognizable — better than blank.
  const subject = (() => {
    if (task.nameText && task.nameText.trim()) return task.nameText.trim();
    if (task.nameKey) {
      const loc = ctx.t(task.nameKey);
      return loc === task.nameKey ? task.nameKey : loc;
    }
    return ctx.t("actionCenter.inbox.generic.task");
  })();

  // Due-date parsing. DateOnly serializes as YYYY-MM-DD; new Date("YYYY-MM-DD")
  // is UTC-midnight which is fine for day-granularity comparisons.
  //
  // Status mapping (DEV-1257-followup, L4E status palette intent):
  //   past due_date              → "overdue" (RED — Critical)
  //   future, within 7 days      → "due"     (YELLOW — Warning / needs attention)
  //   future, beyond 7 days      → "ongoing" (GREEN — On Track)
  //   no due date                → "ongoing" (GREEN — exists but no deadline pressure)
  //
  // The 7-day threshold matches the locked rule that YELLOW is a
  // near-term "needs attention" signal, not the default for any future
  // deadline. Comfortable-runway tasks read as on-track work, freeing
  // YELLOW to actually mean something. dueInDays is still populated so
  // the priority score continues to de-rank comfortably-far items below
  // imminent ones — color is orthogonal to ranking.
  const MP_TASK_DUE_SOON_DAYS = 7;
  let status: ActionStatus;
  let overdueDays = 0;
  let dueInDays: number | null = null;
  let dateSort = NO_DATE;
  let dateLabel = ctx.t("actionCenter.inbox.noDueDate");
  if (task.dueDate) {
    const due = new Date(task.dueDate + "T00:00:00");
    if (!Number.isNaN(due.getTime())) {
      dateSort = due.getTime();
      dateLabel = shortDate(due.toISOString());
      const delta = Math.round((due.getTime() - ctx.now.getTime()) / DAY_MS);
      if (delta < 0) {
        status = "overdue";
        overdueDays = -delta;
      } else if (delta <= MP_TASK_DUE_SOON_DAYS) {
        status = "due";
        dueInDays = delta;
      } else {
        // Comfortable runway — on track, not warning-worthy yet.
        status = "ongoing";
        dueInDays = delta;
      }
    } else {
      status = "ongoing";
    }
  } else {
    // No due date → not actionable on a deadline. Surface as ongoing
    // so it ranks below dated work but doesn't disappear.
    status = "ongoing";
  }

  // Role grouping (§4a): a project task groups under its mini-project's OWNING
  // COMMUNITY (org), with the project itself as the nested role (project pillar).
  // Falls back to "other" only when the owning community is unknown.
  const roleGroupKey = row.ownerCommunityGuid
    ? `community:${row.ownerCommunityGuid.toUpperCase()}`
    : ROLE_OTHER_KEY;
  return {
    id: `mp_task:${task.taskGuid}`,
    kind: "mp_task",
    typeKey: TYPE_KEY.mp_task,
    source: row.miniProjectName || ctx.t("actionCenter.inbox.generic.task"),
    roleGroupKey,
    roleGroupLabel: row.ownerCommunityName || row.miniProjectName || undefined,
    role: row.miniProjectName || undefined,
    pillar: "project",
    subject,
    description: task.description ?? undefined,
    dateLabel,
    dateSort,
    addedLabel: task.createdAt ? shortDate(task.createdAt) : "—",
    addedSort: task.createdAt ? new Date(task.createdAt).getTime() : NO_DATE,
    status,
    overdue: status === "overdue",
    base: TYPE_BASE.mp_task,
    overdueDays,
    dueInDays,
    ageDays: 0,
    mpTask: row,
  };
}

// ════════════════════════════════════════════════════════════════
// Gantt RACI tasks  (DEV-1368) — the user's A/C/I (and R) per role
// ════════════════════════════════════════════════════════════════

/**
 * Convert one MyRaciTask (composed by useMyRaciTasks across the user's
 * full-project roles) into an ActionItem. The `raci` letter feeds the RACI
 * column; status places the row by each type's own "done" meaning:
 *   R / A  → urgency (overdue / imminent / later); done at 100%
 *   C      → needs your input until you respond
 *   I      → needs your acknowledgment until you ack
 * Done items carry status "resolved" so the page routes them to Completed.
 */
export function raciTaskToActionItem(
  rt: MyRaciTask,
  ctx: { t: (k: string) => string },
): ActionItem {
  const end = rt.endDate ? new Date(rt.endDate) : null;
  const dateSort = end ? end.getTime() : Number.MAX_SAFE_INTEGER;
  const dateLabel = end
    ? end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";
  const today = new Date();
  const dueInDays = end
    ? Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const overdue = !rt.done && rt.urgency === "overdue";

  // A Responsible Gantt task that has STARTED and isn't 100% is active work →
  // Current/To-Do (Skip 2026-06-23). Overdue stays overdue; not-yet-started
  // tasks are Upcoming. (Per-reporting-cadence resurfacing for long tasks is a
  // follow-up that needs the project reporting cadence + advance-notice data.)
  const started = rt.startDate ? new Date(rt.startDate).getTime() <= Date.now() : false;
  let status: ActionStatus;
  if (rt.done) status = "resolved";
  else if (rt.raci === "C") status = "needs_response";
  else if (rt.raci === "I") status = "needs_response";
  else if (rt.urgency === "overdue") status = "overdue";
  else status = started ? "due" : "ongoing";

  return {
    id: `raci:${rt.projectGuid}:${rt.roleSlotGuid}:${rt.taskGuid}`.toUpperCase(),
    kind: "raci",
    typeKey: TYPE_KEY.raci,
    source: ctx.t("actionCenter.inbox.type.projectTask"),
    raci: rt.raci,
    subject: rt.taskName,
    dateLabel,
    dateSort,
    status,
    overdue,
    base: TYPE_BASE.raci,
    overdueDays: overdue && dueInDays !== null ? Math.abs(dueInDays) : 0,
    dueInDays,
    ageDays: 0,
    raciTask: rt,
  };
}

// ════════════════════════════════════════════════════════════════
// Data entry  (per project + section, Option B / Q6)
// ════════════════════════════════════════════════════════════════

// Nominal day offsets — exact per-instance day math is a documented
// refinement (POLICY_ACTION_CENTER.md §Q3 open item). Overdue sections rank
// as a missed reporting period; due sections as a near deadline.
const DATA_OVERDUE_NOMINAL = 7;
const DATA_DUE_NOMINAL = 3;

interface SectionCounts {
  due: number;
  overdue: number;
}

export function dataRowToActionItems(
  row: any,
  sectionCounts: Record<DataSection, SectionCounts>,
  ctx: { t: (k: string) => string },
): ActionItem[] {
  const projectGuid = String(row?.project_guid || row?.project_GUID || "").toUpperCase();
  const projectName = String(row?.project?.project_name || row?.project_name || "(unnamed)");
  const out: ActionItem[] = [];
  (["mission", "schedule", "finance"] as DataSection[]).forEach((section) => {
    const c = sectionCounts[section];
    if (!c || (c.due === 0 && c.overdue === 0)) return;
    const overdue = c.overdue > 0;
    out.push({
      id: `data:${projectGuid}:${section}`,
      kind: "data",
      typeKey: TYPE_KEY.data,
      source: projectName,
      // Data entry is a project-wide obligation with no specific role — label
      // the Role column "Team" rather than a blank "—" (Skip, DEV-1256 follow-up).
      role: ctx.t("actionCenter.inbox.role.team"),
      subject: ctx.t(`actionCenter.inbox.dataSection.${section}`),
      dateLabel: ctx.t("actionCenter.inbox.thisPeriod"),
      dateSort: Date.now(),
      status: overdue ? "overdue" : "due",
      overdue,
      base: TYPE_BASE.data,
      overdueDays: overdue ? DATA_OVERDUE_NOMINAL : 0,
      dueInDays: overdue ? null : DATA_DUE_NOMINAL,
      ageDays: 0,
      data: { projectGuid, projectName, section, row },
    });
  });
  return out;
}

// ════════════════════════════════════════════════════════════════
// System tasks  (onboarding milestones)
// ════════════════════════════════════════════════════════════════

export function milestoneToActionItem(
  m: any,
  ctx: { t: (k: string) => string },
): ActionItem {
  // MilestoneUnlock shape (archetypeConfig.ts): { milestone, severity,
  // nudgeKey, ctaRoute?, ctaLabelKey? }. The display title is a separate i18n
  // key: onboarding.milestones.<key>.label (matches the legacy Tasks tab).
  const key = String(m?.milestone || m?.key || "");
  const subject = key
    ? ctx.t(`onboarding.milestones.${key}.label`)
    : ctx.t("actionCenter.inbox.generic.system");
  return {
    id: `system:${key || Math.random()}`,
    kind: "system",
    typeKey: TYPE_KEY.system,
    source: ctx.t("actionCenter.inbox.source.gettingStarted"),
    // Role #0 — the base "L4E User" role. roleGroupLabel stays "Life4Earth"; the
    // page appends the trust rung for display ("Life4Earth — {rung}", §2.1).
    roleGroupKey: ROLE_BASE_KEY,
    roleGroupLabel: "Life4Earth",
    pillar: "base",
    subject,
    dateLabel: "—",
    dateSort: NO_DATE,
    status: "due",
    overdue: false,
    base: TYPE_BASE.system,
    overdueDays: 0,
    dueInDays: null,
    ageDays: 0,
    milestone: m,
  };
}

// ════════════════════════════════════════════════════════════════
// Getting-started checklist  (onboarding chunk A — §7.7 r1 / §7.9)
// ════════════════════════════════════════════════════════════════

/**
 * Inputs for the §7.7 Getting-started checklist. Completion computes from
 * LIVE data — profile fields, follow count, ack record, profile flags —
 * the same computed-not-stored pattern as the rung basics (§7.10 r4: NO
 * event/atom framework in chunk A).
 */
export interface GettingStartedInputs {
  /** Rung-basic: display name + photo + bio (mirrors OnboardingRung). */
  profileComplete: boolean;
  /** Live follow count (entity_follows). Rung-basic threshold = 3. */
  followCount: number;
  /** Server-side profile flags (user_milestone_completions slugs). */
  achievedFlags: ReadonlySet<string>;
  /** The inviting org community (lineage edge) — null for self-serve users. */
  orgCommunityGuid: string | null;
  orgCommunityName: string | null;
  /** Org guidelines state; null while loading or when no org. */
  guidelines: { hasContent: boolean; acknowledged: boolean } | null;
  /** Registration timestamp — the checklist obligations date from signup,
   *  so the Added column shows a real date, not "—". */
  signupAt?: string | null;
  /** Platform community-standards currency (voice-ladder gate, Skip
   *  2026-07-07): acknowledged version >= STANDARDS_CURRENT_VERSION.
   *  Drives the "Read this: community standards" act — a rung basic AND
   *  the org-scoped posting unlock. */
  standardsCurrent: boolean;
}

const GS_BASE = {
  kind: "system" as ActionKind,
  typeKey: TYPE_KEY.system,
  roleGroupKey: ROLE_BASE_KEY,
  roleGroupLabel: "Life4Earth",
  pillar: "base" as RolePillar,
  dateLabel: "—",
  dateSort: NO_DATE,
  overdue: false,
  base: TYPE_BASE.system,
  overdueDays: 0,
  dueInDays: null,
  ageDays: 0,
  gettingStarted: true,
};

/**
 * Build the §7.7 r1 Getting-started rows (Explorer runway to Contributor):
 *   1. complete profile (photo+bio) → /profile          [rung basic]
 *   2. follow 3 projects/communities → /discover        [rung basic]
 *   3. "Meet your organization" → org feed              [orientation, dismissible]
 *   4. "Read this: {Org} guidelines" → Notice/ack row   [only if designated, §7.9]
 * The pre-assigned role invitation (§7.7 item 1) is NOT built here — it is
 * the existing workflow Invitation card and rides the event stream.
 * Completed rows are returned with status "resolved" and stay visible
 * (gettingStarted flag) so the checklist reads as a checklist.
 * Everything is kind=system / source "Getting started" / role:base at the
 * lowest base priority — it never crowds real obligations (locked rule 3).
 */
export function gettingStartedToActionItems(
  inputs: GettingStartedInputs,
  ctx: { t: (k: string, vars?: Record<string, any>) => string },
): ActionItem[] {
  const out: ActionItem[] = [];
  const source = ctx.t("actionCenter.inbox.source.gettingStarted");
  // Every checklist row became an obligation at signup — that's its Added date.
  const addedLabel = shortDate(inputs.signupAt) || undefined;
  const addedSort = inputs.signupAt ? new Date(inputs.signupAt).getTime() : undefined;

  const push = (
    key: string,
    complete: boolean,
    subject: string,
    milestone: Record<string, any>,
    kind: ActionKind = "system",
  ) => {
    out.push({
      ...GS_BASE,
      id: `gs:${key}`,
      kind,
      typeKey: TYPE_KEY[kind],
      base: TYPE_BASE[kind],
      source,
      // The checklist is the Explorer runway — every row is an Explorer
      // task, so say so in the Role column (Skip, 2026-07-08).
      role: ctx.t("actionCenter.inbox.rung.explorer"),
      subject,
      addedLabel,
      addedSort,
      status: complete ? "resolved" : kind === "notice" ? "needs_response" : "due",
      checklistKey: key,
      milestone: {
        ...milestone,
        checklistKey: key,
        // Pre-resolve the tray body through the caller's t so the admin
        // copy-override layer (onboarding_copy) applies to tray text too —
        // the tray renders nudgeText when present, falling back to its own
        // t(nudgeKey) for legacy items.
        nudgeText: milestone.nudgeKey ? ctx.t(milestone.nudgeKey) : undefined,
      },
    });
  };

  // 0 — "Read me first: welcome to Life4Earth" (Skip 2026-07-07). The §7.9
  // pointer to the WelcomeScreenModal (the durable content — it re-opens
  // from here and from the admin preview). Reading it IS the acknowledgment.
  push(
    "read_me_first",
    inputs.achievedFlags.has("read_me_first_ack"),
    ctx.t("actionCenter.inbox.gettingStarted.readMeFirst.label"),
    {
      nudgeKey: "actionCenter.inbox.gettingStarted.readMeFirst.nudge",
    },
    "notice",
  );

  // 1 — complete profile (photo + bio). Reuses the existing milestone keys.
  push(
    "complete_profile",
    inputs.profileComplete,
    ctx.t("onboarding.milestones.complete_profile.label"),
    {
      milestone: "complete_profile",
      nudgeKey: "onboarding.nudges.explorer.completeProfile",
      ctaRoute: "/profile",
      // Land on About (photo + short bio live there), not the default
      // Impact tab — rides the DEV-991 initialTab navigation state.
      ctaState: { initialTab: "about" },
      ctaLabelKey: "onboarding.cta.completeProfile",
    },
  );

  // 2 — follow 3 projects/communities.
  push(
    "follow_three",
    inputs.followCount >= 3,
    ctx.t("actionCenter.inbox.gettingStarted.followThree.label"),
    {
      milestone: "follow_three",
      nudgeKey: "actionCenter.inbox.gettingStarted.followThree.nudge",
      ctaRoute: "/discover",
      ctaLabelKey: "actionCenter.inbox.gettingStarted.followThree.cta",
    },
  );

  // 3 — "Read this: Life4Earth community standards" — the deliberate
  // acknowledgment ACT (§7.5/§7.9 as amended 2026-07-07). Notice-type
  // pointer; the CONTENT is the CommunityStandardsModal (read-to-accept
  // with proof-of-read — no bare acknowledge that skips the reading).
  // Completing it is a rung basic AND unlocks org-scoped posting.
  push(
    "platform_standards",
    inputs.standardsCurrent,
    ctx.t("actionCenter.inbox.gettingStarted.standards.label"),
    {
      nudgeKey: "actionCenter.inbox.gettingStarted.standards.nudge",
    },
    "notice",
  );

  // 4 — "Meet your organization" (orientation; org-invited users only).
  const orgLabel =
    inputs.orgCommunityName ||
    ctx.t("actionCenter.inbox.gettingStarted.orgFallback");
  if (inputs.orgCommunityGuid) {
    push(
      "meet_org",
      inputs.achievedFlags.has("org_orientation_done"),
      ctx.t("actionCenter.inbox.gettingStarted.meetOrg.label", { org: orgLabel }),
      {
        milestone: "org_orientation_done",
        nudgeKey: "actionCenter.inbox.gettingStarted.meetOrg.nudge",
        ctaRoute: `/communities/${inputs.orgCommunityGuid}#feed`,
        ctaLabelKey: "actionCenter.inbox.gettingStarted.meetOrg.cta",
        communityGuid: inputs.orgCommunityGuid,
      },
    );
  }

  // 5 — "Read this: {Org} guidelines" — Notice-type acknowledge row (§7.9).
  // Appears ONLY when the org has designated guidelines content. The row is a
  // POINTER: acknowledging dismisses it; the material stays on the org page.
  if (inputs.orgCommunityGuid && inputs.guidelines?.hasContent) {
    push(
      "org_guidelines",
      inputs.guidelines.acknowledged,
      ctx.t("actionCenter.inbox.gettingStarted.readGuidelines.label", {
        org: orgLabel,
      }),
      {
        nudgeKey: "actionCenter.inbox.gettingStarted.readGuidelines.nudge",
        communityGuid: inputs.orgCommunityGuid,
      },
      "notice",
    );
  }

  return out;
}

/**
 * §7.7 r2 — the Explorer→Contributor celebration Notice. Renders while the
 * server-written fired-flag is present and unacknowledged; acknowledging
 * records `contributor_promotion_ack` and clears it. This is the SKIN over
 * the trigger — a richer hero moment can replace this item without touching
 * the promotion detection in OnboardingController.
 */
export function promotionNoticeToActionItem(ctx: {
  t: (k: string) => string;
  /** When the promotion fired (the contributor_promoted flag timestamp). */
  promotedAt?: string | null;
}): ActionItem {
  return {
    ...GS_BASE,
    id: "gs:promotion_notice",
    kind: "notice",
    typeKey: TYPE_KEY.notice,
    base: TYPE_BASE.notice,
    gettingStarted: false, // a real one-time Notice — resolves away, not a checklist row
    source: "Life4Earth",
    subject: ctx.t("actionCenter.inbox.gettingStarted.promotion.subject"),
    addedLabel: shortDate(ctx.promotedAt) || undefined,
    addedSort: ctx.promotedAt ? new Date(ctx.promotedAt).getTime() : undefined,
    status: "needs_response",
    checklistKey: "promotion_notice",
    milestone: {
      checklistKey: "promotion_notice",
      nudgeKey: "actionCenter.inbox.gettingStarted.promotion.body",
    },
  };
}

/**
 * Open (not-yet-satisfied) Getting-started row count — feeds the LeftNav
 * Action Center badge for the Explorer cohort (Skip, 2026-07-07: a day-zero
 * user with 4 open checklist items saw no badge because approvalsBadge
 * counts only workflow events). Labels are irrelevant to the count, so a
 * passthrough t keeps this callable without i18n context.
 */
export function gettingStartedOpenCount(inputs: GettingStartedInputs): number {
  return gettingStartedToActionItems(inputs, { t: (k: string) => k }).filter(
    (it) => it.status !== "resolved",
  ).length;
}

// ════════════════════════════════════════════════════════════════
// Sent view (outbound events)
// ════════════════════════════════════════════════════════════════

export type SentStatus = "awaiting" | "accepted" | "declined";

export interface SentItem {
  id: string;
  kind: ActionKind;
  typeKey: string;
  to: string;
  subject: string;
  dateLabel: string;
  dateSort: number;
  status: SentStatus;
  workflow: WorkflowRequest;
}

export function workflowToSentItem(
  w: WorkflowRequest,
  ctx: EventAdapterCtx,
): SentItem {
  const kind = eventKind(w.action_type);
  let status: SentStatus = "awaiting";
  if (w.status === "approved") status = "accepted";
  else if (w.status === "rejected" || w.status === "rescinded") {
    status = "declined";
  }
  const toGuid = (w.assigned_to_guids || w.assigned_to_user_guids || [])[0];
  const toName = toGuid ? ctx.resolveName(toGuid) || String(toGuid).slice(0, 8) : "—";
  return {
    id: w.workflow_guid,
    kind,
    typeKey: TYPE_KEY[kind],
    to: toName,
    subject: eventSubject(w, kind, ctx.t),
    dateLabel: shortDate(w.created_at),
    dateSort: w.created_at ? new Date(w.created_at).getTime() : NO_DATE,
    status,
    workflow: w,
  };
}

// ── Status → i18n label key (Status column) ────────────────────
export function statusKey(s: ActionStatus): string {
  switch (s) {
    case "needs_response":
      return "actionCenter.inbox.status.needsResponse";
    case "due":
      return "actionCenter.inbox.status.due";
    case "overdue":
      return "actionCenter.inbox.status.overdue";
    case "snoozed":
      return "actionCenter.inbox.status.snoozed";
    case "ongoing":
      return "actionCenter.inbox.status.ongoing";
    case "resolved":
      return "actionCenter.inbox.status.resolved";
    default:
      return "actionCenter.inbox.status.due";
  }
}
